export type SvgOutputFormat = 'png' | 'jpg'

export type SvgConversionErrorCode =
  | 'unsupported_type'
  | 'too_large'
  | 'invalid_svg'
  | 'unsafe_svg'
  | 'external_resource'
  | 'unsupported_svg'
  | 'canvas_error'
  | 'conversion_failed'

export interface SvgErrorCopy {
  unsupported: string
  tooLarge: string
  invalidSvg: string
  unsafeSvg: string
  externalResource: string
  unsupportedSvg: string
  canvas: string
  conversion: string
}

export interface LoadedSvg {
  file: File
  url: string
  width: number
  height: number
  dimensionsDetected: boolean
}

export interface SvgConversionResult {
  blob: Blob
  url: string
  width: number
  height: number
  format: SvgOutputFormat
}

export class SvgConversionError extends Error {
  constructor(
    message: string,
    readonly code: SvgConversionErrorCode,
  ) {
    super(message)
  }
}

const MAX_FILE_SIZE = 10 * 1024 * 1024
const MAX_SERIALIZED_SIZE = 10 * 1024 * 1024
const MAX_ELEMENT_COUNT = 10_000
const MAX_ATTRIBUTE_COUNT = 10_000
const MAX_NESTING_DEPTH = 256
const MAX_PIXELS = 36_000_000
const MAX_DIMENSION = 12_000
const DEFAULT_WIDTH = 1024
const DEFAULT_HEIGHT = 1024
const SVG_NS = 'http://www.w3.org/2000/svg'

const BLOCKED_ELEMENTS = new Set([
  'script',
  'foreignobject',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
  'set',
  'animate',
  'animatetransform',
  'animatemotion',
  'animatecolor',
  'discard',
])

const RESOURCE_ATTRS = new Set(['href', 'xlink:href', 'src'])

export function isSvgFile(file: File): boolean {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  return type === 'image/svg+xml' || type === 'text/xml' || name.endsWith('.svg')
}

export async function prepareSvgFile(
  file: File,
  errors: SvgErrorCopy,
): Promise<LoadedSvg> {
  if (!isSvgFile(file)) {
    throw new SvgConversionError(errors.unsupported, 'unsupported_type')
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new SvgConversionError(errors.tooLarge, 'too_large')
  }

  const source = await file.text()
  const sanitized = sanitizeSvg(source, errors)
  const blob = new Blob([sanitized], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)

  try {
    const image = await loadImage(url, errors)
    const dimensions = extractDimensions(sanitized, image.naturalWidth, image.naturalHeight)
    return {
      file,
      url,
      width: dimensions.width,
      height: dimensions.height,
      dimensionsDetected: dimensions.detected,
    }
  } catch (error) {
    URL.revokeObjectURL(url)
    if (error instanceof SvgConversionError) {
      throw error
    }
    throw new SvgConversionError(errors.unsupportedSvg, 'unsupported_svg')
  }
}

export async function convertSvg(
  svg: LoadedSvg,
  format: SvgOutputFormat,
  targetWidth: number,
  targetHeight: number,
  backgroundColor: string,
  errors: SvgErrorCopy,
): Promise<SvgConversionResult> {
  const width = validateDimension(targetWidth, errors)
  const height = validateDimension(targetHeight, errors)

  if (width * height > MAX_PIXELS) {
    throw new SvgConversionError(errors.canvas, 'canvas_error')
  }

  try {
    const image = await loadImage(svg.url, errors)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')
    if (!context) {
      throw new SvgConversionError(errors.canvas, 'canvas_error')
    }

    if (format === 'jpg') {
      context.fillStyle = normalizeColor(backgroundColor)
      context.fillRect(0, 0, width, height)
    }

    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
    context.drawImage(image, 0, 0, width, height)

    const blob = await blobFromCanvas(canvas, format, errors)
    return {
      blob,
      url: URL.createObjectURL(blob),
      width,
      height,
      format,
    }
  } catch (error) {
    if (error instanceof SvgConversionError) {
      throw error
    }
    throw new SvgConversionError(errors.conversion, 'conversion_failed')
  }
}

export function outputFilename(inputName: string, format: SvgOutputFormat): string {
  const base = inputName.replace(/\.[^.]+$/, '') || 'image'
  return `${base}.${format === 'jpg' ? 'jpg' : 'png'}`
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

export function sanitizeSvg(source: string, errors: SvgErrorCopy): string {
  if (/<!\s*(?:DOCTYPE|ENTITY)\b/i.test(source)) {
    throw new SvgConversionError(errors.unsafeSvg, 'unsafe_svg')
  }
  if (hasExcessiveStructure(source)) {
    throw new SvgConversionError(errors.unsafeSvg, 'unsafe_svg')
  }

  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(source, 'image/svg+xml')
    const parserError = doc.querySelector('parsererror')
    const root = doc.documentElement

    if (
      parserError ||
      !root ||
      root.localName.toLowerCase() !== 'svg' ||
      root.namespaceURI !== SVG_NS
    ) {
      throw new SvgConversionError(errors.invalidSvg, 'invalid_svg')
    }

    walkSvg(root, errors)

    if (!root.getAttribute('xmlns')) {
      root.setAttribute('xmlns', SVG_NS)
    }

    const serialized = new XMLSerializer().serializeToString(root)
    if (new Blob([serialized]).size > MAX_SERIALIZED_SIZE) {
      throw new SvgConversionError(errors.unsafeSvg, 'unsafe_svg')
    }
    return serialized
  } catch (error) {
    if (error instanceof SvgConversionError) throw error
    throw new SvgConversionError(errors.invalidSvg, 'invalid_svg')
  }
}

function walkSvg(root: Element, errors: SvgErrorCopy): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT)
  let elementCount = 0
  inspectElement(root, errors)
  elementCount += 1
  let node = walker.nextNode()

  while (node) {
    if (node instanceof Element) {
      elementCount += 1
      if (elementCount > MAX_ELEMENT_COUNT) {
        throw new SvgConversionError(errors.unsafeSvg, 'unsafe_svg')
      }
      inspectElement(node, errors)
    }
    node = walker.nextNode()
  }
}

function inspectElement(element: Element, errors: SvgErrorCopy): void {
  const tag = element.localName.toLowerCase()
  if (BLOCKED_ELEMENTS.has(tag)) {
    throw new SvgConversionError(errors.unsafeSvg, 'unsafe_svg')
  }

  if (tag === 'style' && unsafeCss(element.textContent ?? '')) {
    throw new SvgConversionError(errors.externalResource, 'external_resource')
  }

  for (const attr of Array.from(element.attributes)) {
    const attrName = attr.name.toLowerCase()
    const attrLocalName = attr.localName.toLowerCase()
    const attrValue = attr.value.trim()

    if (attrName === 'xml:base' || attrLocalName === 'base') {
      throw new SvgConversionError(errors.externalResource, 'external_resource')
    }

    if (attrLocalName.startsWith('on') || unsafeUrlValue(attrValue)) {
      throw new SvgConversionError(errors.unsafeSvg, 'unsafe_svg')
    }

    if (unsafeCss(attrValue)) {
      throw new SvgConversionError(errors.externalResource, 'external_resource')
    }

    if (
      RESOURCE_ATTRS.has(attrLocalName) &&
      attrValue &&
      !attrValue.startsWith('#')
    ) {
      throw new SvgConversionError(errors.externalResource, 'external_resource')
    }
  }
}

function hasExcessiveStructure(source: string): boolean {
  // Scan conservatively before DOMParser. Besides elements, bound attributes
  // because a single tag with thousands of attributes can exhaust memory in
  // the parser before the post-parse walker gets a chance to reject it.
  let elementCount = 0
  let attributeCount = 0
  let nestingDepth = 0

  let index = 0
  while (index < source.length) {
    if (source[index] !== '<') {
      index += 1
      continue
    }

    if (source.startsWith('<!--', index)) {
      const commentEnd = source.indexOf('-->', index + 4)
      index = commentEnd === -1 ? source.length : commentEnd + 3
      continue
    }
    if (source.startsWith('<![CDATA[', index)) {
      const cdataEnd = source.indexOf(']]>', index + 9)
      index = cdataEnd === -1 ? source.length : cdataEnd + 3
      continue
    }

    const marker = source[index + 1]
    if (marker === '/') {
      nestingDepth = Math.max(0, nestingDepth - 1)
      const declarationEnd = source.indexOf('>', index + 2)
      index = declarationEnd === -1 ? source.length : declarationEnd + 1
      continue
    }
    if (marker === '!' || marker === '?') {
      const declarationEnd = source.indexOf('>', index + 2)
      index = declarationEnd === -1 ? source.length : declarationEnd + 1
      continue
    }

    let cursor = index + 1
    while (/\s/u.test(source[cursor] ?? '')) cursor += 1
    if (source[cursor] === '!' || source[cursor] === '?') {
      const declarationEnd = source.indexOf('>', cursor + 1)
      index = declarationEnd === -1 ? source.length : declarationEnd + 1
      continue
    }

    const nameStart = cursor
    while (
      cursor < source.length &&
      !/[\s/>]/u.test(source[cursor] ?? '')
    ) {
      cursor += 1
    }
    if (cursor === nameStart) {
      index += 1
      continue
    }

    elementCount += 1
    if (elementCount > MAX_ELEMENT_COUNT) return true

    let selfClosing = false
    while (cursor < source.length) {
      const character = source[cursor]
      if (character === '>') {
        index = cursor + 1
        break
      }
      if (/\s/u.test(character ?? '') || character === '/') {
        if (character === '/') selfClosing = true
        cursor += 1
        continue
      }

      const attributeStart = cursor
      while (
        cursor < source.length &&
        !/[\s=/>]/u.test(source[cursor] ?? '')
      ) {
        cursor += 1
      }
      if (cursor === attributeStart) {
        cursor += 1
        continue
      }

      attributeCount += 1
      if (attributeCount > MAX_ATTRIBUTE_COUNT) {
        return true
      }

      while (/\s/u.test(source[cursor] ?? '')) cursor += 1
      if (source[cursor] !== '=') continue
      cursor += 1
      while (/\s/u.test(source[cursor] ?? '')) cursor += 1

      const quote = source[cursor]
      if (quote === '"' || quote === "'") {
        cursor += 1
        while (cursor < source.length && source[cursor] !== quote) {
          cursor += 1
        }
        if (cursor < source.length) cursor += 1
      } else {
        while (cursor < source.length && !/[\s>]/u.test(source[cursor] ?? '')) {
          cursor += 1
        }
      }
    }
    if (!selfClosing) {
      nestingDepth += 1
      if (nestingDepth > MAX_NESTING_DEPTH) return true
    }
    if (cursor >= source.length) index = source.length
  }

  return false
}

function unsafeUrlValue(value: string): boolean {
  const normalized = value.replace(/[\u0000-\u0020\u007f]+/g, '')
  return /^(javascript|vbscript|data:text\/html):/i.test(normalized)
}

function unsafeCss(value: string): boolean {
  const normalized = decodeCssEscapes(value).replace(/\/\*[\s\S]*?\*\//g, '')
  if (/@import/i.test(normalized)) return true
  if (/(?:^|[^\w-])(?:image|image-set|cross-fade|element|src)\s*\(/i.test(normalized)) {
    return true
  }
  const urlPattern = /\burl\s*\(\s*(?:(['"])(.*?)\1|([^)]*))\s*\)/gi
  let urlMatch = urlPattern.exec(normalized)
  while (urlMatch) {
    const url = (urlMatch[2] ?? urlMatch[3] ?? '').trim()
    if (!url.startsWith('#')) return true
    urlMatch = urlPattern.exec(normalized)
  }
  if (/\burl\s*\(/i.test(normalized) && !urlPattern.test(normalized)) return true
  return /['"]\s*(?:https?:|\/\/|data:|\/|\.\.?\/)/i.test(normalized)
}

function decodeCssEscapes(value: string): string {
  return value.replace(
    /\\([0-9a-f]{1,6})\s?|\\(.)/gi,
    (_, hex: string | undefined, char: string | undefined) => {
      if (hex) {
        const codePoint = Number.parseInt(hex, 16)
        return codePoint > 0 && codePoint <= 0x10ffff
          ? String.fromCodePoint(codePoint)
          : ''
      }
      return char ?? ''
    },
  )
}

export function extractDimensions(
  sanitized: string,
  naturalWidth: number,
  naturalHeight: number,
): { width: number; height: number; detected: boolean } {
  const doc = new DOMParser().parseFromString(sanitized, 'image/svg+xml')
  const root = doc.documentElement
  const width = parseSvgLength(root.getAttribute('width'))
  const height = parseSvgLength(root.getAttribute('height'))

  if (width && height) {
    return { width, height, detected: true }
  }

  const viewBoxValues = root.getAttribute('viewBox')?.trim().split(/[\s,]+/).map(Number)
  if (viewBoxValues?.length === 4) {
    const [, , viewBoxWidth, viewBoxHeight] = viewBoxValues
    if (
      typeof viewBoxWidth === 'number' &&
      typeof viewBoxHeight === 'number' &&
      Number.isFinite(viewBoxWidth) &&
      Number.isFinite(viewBoxHeight) &&
      viewBoxWidth > 0 &&
      viewBoxHeight > 0
    ) {
      return {
        width: Math.round(viewBoxWidth),
        height: Math.round(viewBoxHeight),
        detected: true,
      }
    }
  }

  if (naturalWidth > 0 && naturalHeight > 0) {
    return { width: naturalWidth, height: naturalHeight, detected: false }
  }

  return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT, detected: false }
}

function parseSvgLength(value: string | null): number | null {
  if (!value) return null
  const match = value.trim().match(/^([0-9]*\.?[0-9]+)\s*(px|pt|pc|in|cm|mm|Q)?$/i)
  if (!match) return null
  const unit = (match[2] ?? 'px').toLowerCase()
  const pixelsPerUnit: Record<string, number> = {
    px: 1,
    pt: 96 / 72,
    pc: 16,
    in: 96,
    cm: 96 / 2.54,
    mm: 96 / 25.4,
    q: 96 / 101.6,
  }
  const pixelsPerUnitValue = pixelsPerUnit[unit]
  if (pixelsPerUnitValue === undefined) return null
  const parsed = Number(match[1]) * pixelsPerUnitValue
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null
}

function loadImage(src: string, errors: SvgErrorCopy): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () =>
      reject(new SvgConversionError(errors.unsupportedSvg, 'unsupported_svg'))
    image.src = src
  })
}

function blobFromCanvas(
  canvas: HTMLCanvasElement,
  format: SvgOutputFormat,
  errors: SvgErrorCopy,
): Promise<Blob> {
  const mime = format === 'jpg' ? 'image/jpeg' : 'image/png'
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new SvgConversionError(errors.conversion, 'conversion_failed'))
          return
        }
        resolve(blob)
      },
      mime,
      format === 'jpg' ? 0.92 : undefined,
    )
  })
}

function validateDimension(value: number, errors: SvgErrorCopy): number {
  if (!Number.isFinite(value) || value < 1 || value > MAX_DIMENSION) {
    throw new SvgConversionError(errors.canvas, 'canvas_error')
  }
  return Math.round(value)
}

function normalizeColor(value: string): string {
  return /^#[0-9a-f]{6}$/i.test(value) ? value : '#ffffff'
}
